import { useState } from 'react';

import { Button } from '../../button';

import {
  againstStrongText,
  billedTokens,
  CACHE_REFINEMENT,
  cacheSummary,
  cacheText,
  cacheTransition,
  CALLS,
  CHOOSE_MODELS,
  chooseModelLabels,
  DISCOUNTS,
  discountLabels,
  dollars,
  EFFORTS,
  escalatedText,
  goldenRuns,
  initialCache,
  initialModels,
  initialRoute,
  initialThink,
  inputCost,
  MODELS_REFINEMENT,
  modelsSummary,
  modelsTransition,
  msText,
  ofEight,
  ofThirty,
  ROUTE_REFINEMENT,
  routeRuns,
  routeSummary,
  routeText,
  routeTransition,
  runText,
  savedDollars,
  SIGNALS,
  signalLabels,
  THINK_MODELS,
  THINK_REFINEMENT,
  thinkModelLabels,
  thinkRun,
  thinkSummary,
  thinkText,
  thinkTransition,
  thousands,
  toCalls,
  toChooseModel,
  toDiscount,
  toEffort,
  toSignal,
  toThinkModel,
  type CacheAction,
  type ModelsAction,
  type RouteAction,
  type ThinkAction,
} from './choose-models';
import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';

import './relationship-labs.css';

function Predict({ children }: { children: string }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
const REPLAY = 'This graphic replays real runs.';
const COMPUTED = 'This graphic computes the bill from a real 2,637-token prefix.';

export function ChooseModelsLab() {
  const [state, setState] = useState(initialModels);
  const act = (action: ModelsAction) => setState((current) => modelsTransition(current, action));
  const summary = modelsSummary(state);
  const run = goldenRuns[state.model];
  return (
    <Lab
      title="What does each model score and cost on the thirty notes?"
      eyebrow="Choosing a model · two columns"
      className="rel-lab choose-lab"
    >
      <Predict>
        What does each model score and cost on the thirty notes? Choose a Model, then press Run the
        thirty notes.
      </Predict>
      <div className="cb-controls">
        <label>
          Model
          <ModelSelect
            aria-label="Model"
            value={state.model}
            onValueChange={(value) => act({ type: 'model', value: toChooseModel(value) })}
            options={CHOOSE_MODELS.map((id) => ({ value: id, label: chooseModelLabels[id] }))}
          />
        </label>
        <Button size="sm" disabled={state.run} onClick={() => act({ type: 'run' })}>
          Run the thirty notes
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The run">
        <pre aria-label="The run">{state.run ? runText(run) : '(not run yet)'}</pre>
      </Panel>
      {state.run ? (
        <Facts
          values={[
            ['Passed', ofThirty(run.passed)],
            ['Cost of the run', dollars(run.cost)],
            ['Latency p50', msText(run.p50)],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.run ? <p className="rel-caption">{MODELS_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function ChooseRouteLab() {
  const [state, setState] = useState(initialRoute);
  const act = (action: RouteAction) => setState((current) => routeTransition(current, action));
  const summary = routeSummary(state);
  const run = routeRuns[state.signal];
  return (
    <Lab
      title="What does the router score, and what does it cost?"
      eyebrow="Choosing a model · the router"
      className="rel-lab choose-lab"
    >
      <Predict>
        What does the router score, and what does it cost? Choose an Escalation signal, then press
        Route the thirty notes.
      </Predict>
      <div className="cb-controls">
        <label>
          Escalation signal
          <ModelSelect
            aria-label="Escalation signal"
            value={state.signal}
            onValueChange={(value) => act({ type: 'signal', value: toSignal(value) })}
            options={SIGNALS.map((id) => ({ value: id, label: signalLabels[id] }))}
          />
        </label>
        <Button size="sm" disabled={state.routed} onClick={() => act({ type: 'route' })}>
          Route the thirty notes
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The route">
        <pre aria-label="The route">{state.routed ? routeText(run) : '(not routed yet)'}</pre>
      </Panel>
      {state.routed ? (
        <Facts
          values={[
            ['Escalated', escalatedText(run)],
            ['Routed score', ofThirty(run.score)],
            ['Cost against GLM 5 alone', againstStrongText(run)],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.routed ? <p className="rel-caption">{ROUTE_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function ChooseThinkingLab() {
  const [state, setState] = useState(initialThink);
  const act = (action: ThinkAction) => setState((current) => thinkTransition(current, action));
  const summary = thinkSummary(state);
  const run = thinkRun(state);
  return (
    <Lab
      title="What does more thinking buy, and what does it cost?"
      eyebrow="Choosing a model · reasoning effort"
      className="rel-lab choose-lab"
    >
      <Predict>
        What does more thinking buy, and what does it cost? Choose a Model and a Reasoning effort,
        then press Ask the eight questions.
      </Predict>
      <div className="cb-controls">
        <label>
          Model
          <ModelSelect
            aria-label="Model"
            value={state.model}
            onValueChange={(value) => act({ type: 'model', value: toThinkModel(value) })}
            options={THINK_MODELS.map((id) => ({ value: id, label: thinkModelLabels[id] }))}
          />
        </label>
        <label>
          Reasoning effort
          <ModelSelect
            aria-label="Reasoning effort"
            value={state.effort}
            onValueChange={(value) => act({ type: 'effort', value: toEffort(value) })}
            options={EFFORTS.map((id) => ({ value: id, label: id }))}
          />
        </label>
        <Button size="sm" disabled={state.asked} onClick={() => act({ type: 'ask' })}>
          Ask the eight questions
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The eight questions">
        <pre aria-label="The eight questions">
          {state.asked ? thinkText(run) : '(not asked yet)'}
        </pre>
      </Panel>
      {state.asked ? (
        <Facts
          values={[
            ['Right', ofEight(run.right)],
            ['Completion tokens', thousands(run.tokens)],
            ['Cut by the budget', String(run.cut)],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.asked ? <p className="rel-caption">{THINK_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function ChooseCacheLab() {
  const [state, setState] = useState(initialCache);
  const act = (action: CacheAction) => setState((current) => cacheTransition(current, action));
  const summary = cacheSummary(state);
  const billed = billedTokens(state.discount, state.calls);
  return (
    <Lab
      title="What would a prompt cache save on the suggester's prefix?"
      eyebrow="Choosing a model · the prefix cache"
      className="rel-lab choose-lab"
    >
      <Predict>
        What would a prompt cache save on the suggester's prefix? Choose a Discount on cached input
        and Calls a day, then press Compute.
      </Predict>
      <div className="cb-controls">
        <label>
          Discount on cached input
          <ModelSelect
            aria-label="Discount on cached input"
            value={state.discount}
            onValueChange={(value) => act({ type: 'discount', value: toDiscount(value) })}
            options={DISCOUNTS.map((id) => ({ value: id, label: discountLabels[id] }))}
          />
        </label>
        <label>
          Calls a day with the same prefix
          <ModelSelect
            aria-label="Calls a day with the same prefix"
            value={String(state.calls)}
            onValueChange={(value) => act({ type: 'calls', value: toCalls(value) })}
            options={CALLS.map((calls) => ({ value: String(calls), label: thousands(calls) }))}
          />
        </label>
        <Button size="sm" disabled={state.computed} onClick={() => act({ type: 'compute' })}>
          Compute
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The arithmetic">
        <pre aria-label="The arithmetic">
          {state.computed ? cacheText(state.discount, state.calls) : '(not computed yet)'}
        </pre>
      </Panel>
      {state.computed ? (
        <Facts
          values={[
            ['Input tokens billed', thousands(billed)],
            ['Cost at $1.00 per million', dollars(inputCost(billed))],
            ['Saved against no cache', dollars(savedDollars(state.discount, state.calls))],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.computed ? <p className="rel-caption">{CACHE_REFINEMENT}</p> : null}
      <p className="rel-caption">{COMPUTED}</p>
    </Lab>
  );
}
